import { useSnackbar } from "notistack";
import React, { useEffect, useState, useCallback } from "react";
import {
  activateUser,
  fetchUsersListsData,
  inactivateUser,
  deleteUser,
  fetchPlansListsData,
  updateUserPlan,
  approveUser,
  rejectUser,
} from "../../utils/apis";
import moment from "moment";
import formatNumber from "../../helpers/formatNumber";
import { AiFillDelete } from "react-icons/ai";
import { MdCheck, MdClose, MdEdit, MdOutlineVpnKey } from "react-icons/md";
import {
  confirmAlert,
  confirmAlertWithPayload,
} from "../../utils/sweet-alert-fire";
import { toTitleCase } from "../../utils/helper-methods";
import EditUserDialog from "./components/EditUserDialog";
import ChangePasswordDialog from "./components/ChangePasswordDialog";

const APPROVAL_FILTERS = [
  { value: "", label: "All users" },
  { value: "pending", label: "Waiting for approval" },
  { value: "rejected", label: "Rejected" },
];

// One badge per account. A sign-up that is waiting or turned down says so; it
// is not "activated" until an admin lets it in. Accounts that are approved, or
// never needed approval, show the usual active / suspended state.
const accountStatus = ({ approvalStatus, isActive }) => {
  if (approvalStatus === "pending") {
    return { label: "PENDING", tone: "pending" };
  }
  if (approvalStatus === "rejected") {
    return { label: "REJECTED", tone: "suspended" };
  }
  return isActive
    ? { label: "ACTIVATED", tone: "active" }
    : { label: "SUSPENDED", tone: "suspended" };
};

const AccountStatusBadge = ({ user }) => {
  const { label, tone } = accountStatus(user);
  return <span className={`acc-status-badge status-badge-${tone}`}>{label}</span>;
};

// The slot is always there, empty or not, so edit / password / delete line up
// down the column whatever the row's state.
const ApprovalActions = ({ user, onApproval }) => {
  const { approvalStatus } = user;
  const needsDecision = approvalStatus === "pending" || approvalStatus === "rejected";

  return (
    <div className="row-action-slot">
      {needsDecision && (
        <>
          <button
            type="button"
            className="row-action row-action-approve"
            onClick={() => onApproval(user, true)}
            title="Approve sign-up"
            aria-label="Approve"
          >
            <MdCheck />
          </button>
          {approvalStatus === "pending" && (
            <button
              type="button"
              className="row-action row-action-reject"
              onClick={() => onApproval(user, false)}
              title="Reject sign-up"
              aria-label="Reject"
            >
              <MdClose />
            </button>
          )}
        </>
      )}
    </div>
  );
};

const UsersList = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [searchValue, setSearchValue] = useState("");
  const [usersList, setUsersList] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editingUser, setEditingUser] = useState(null);
  const [passwordUser, setPasswordUser] = useState(null);
  // "" is everyone; the others narrow to sign-ups that are waiting or turned down
  const [approvalFilter, setApprovalFilter] = useState("");

  const USERS_PER_PAGE = 10;

  const handleStatusChange = async (e) => {
    const userId = e.target.id;
    const isActive = e.target.value === "active";
    const res = isActive
      ? await activateUser(userId, enqueueSnackbar)
      : await inactivateUser(userId, enqueueSnackbar);

    setUsersList((prev) =>
      prev.map((user) =>
        user._id === res._id ? { ...user, isActive: res.isActive } : user
      )
    );
  };

  const handleApproval = async (user, approve) => {
    const name = [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email;
    const proceed = await confirmAlertWithPayload({
      title: approve ? "Approve this sign-up?" : "Reject this sign-up?",
      text: approve
        ? `${name} will be emailed and can sign in to the console right away.`
        : `${name} will not be able to sign in. You can still approve them later.`,
    });
    if (!proceed) return;

    const res = approve
      ? await approveUser(user._id, enqueueSnackbar)
      : await rejectUser(user._id, enqueueSnackbar);
    if (res) fetchData();
  };

  const handlePlanChange = async (userId, value) => {
    if (value) await updateUserPlan({ userId, planId: value }, enqueueSnackbar);
  };

  const fetchPlansData = useCallback(async () => {
    const plansData = await fetchPlansListsData(enqueueSnackbar);
    setPlans(plansData);
  }, [enqueueSnackbar]);

  const fetchData = useCallback(async () => {
    try {
      const filters = {
        search: searchValue,
        perPage: USERS_PER_PAGE,
        page: page,
        orderBy: "createdAt",
        orderDirection: "asc",
        isActive: "true,false",
        blocked: "true,false",
        type: "USER",
        approvalStatus: approvalFilter,
      };

      const response = await fetchUsersListsData(filters, enqueueSnackbar);

      let usersData = [];
      let paginationData = null;

      if (response?.data && Array.isArray(response.data)) {
        usersData = response.data;
        paginationData = response?._metadata?.pagination;
      } else if (Array.isArray(response)) {
        usersData = response;
        paginationData = { totalPage: 1 };
      } else if (typeof response === "object" && response._id) {
        usersData = [response];
        paginationData = { totalPage: 1 };
      }

      setUsersList(usersData);
      setTotalPages(paginationData?.totalPage || 1);
    } catch (error) {
      enqueueSnackbar("Error fetching users", { variant: "error" });
    }
  }, [enqueueSnackbar, page, searchValue, approvalFilter]);

  const handleDelete = async (id) => {
    const proceed = await confirmAlert();
    if (!proceed) return;

    const response = await deleteUser(id, enqueueSnackbar);

    if (response?.status === 200) {
      fetchData();
      enqueueSnackbar("User deleted successfully", {
        variant: "success",
        autoHideDuration: 3000,
      });
    }
  };

  useEffect(() => {
    fetchData();
    fetchPlansData();
  }, [fetchData, fetchPlansData]);

  useEffect(() => {
    if (usersList.length === 0) {
      setFilteredUsers([]);
      return;
    }

    let filtered = usersList;

    if (searchValue) {
      const searchLower = searchValue.toLowerCase();
      filtered = usersList.filter(
        (user) =>
          user.firstName?.toLowerCase().includes(searchLower) ||
          user.lastName?.toLowerCase().includes(searchLower) ||
          user.companyName?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredUsers(filtered);
  }, [usersList, searchValue]);

  const handlePrevPage = () => page > 1 && setPage(page - 1);
  const handleNextPage = () => page < totalPages && setPage(page + 1);
  const handleSearchInput = (e) => {
    setSearchValue(e.target.value);
    setPage(1);
  };
  const handleApprovalFilter = (value) => {
    setApprovalFilter(value);
    setPage(1);
  };

  return (
    <div className="users-list-screen">
      {" "}
      <div className="d-flex align-items-center flex-wrap">
        {" "}
        <div className="form-group has-search">
          {" "}
          <svg
            className="form-search-feedback"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>{" "}
          <input
            type="text"
            className="form-control primary-form-control"
            placeholder="Search"
            value={searchValue}
            onChange={handleSearchInput}
          />{" "}
        </div>{" "}
        <div
          className="segmented ms-3"
          role="group"
          aria-label="Filter by approval"
        >
          {APPROVAL_FILTERS.map(({ value, label }) => (
            <button
              key={value || "all"}
              type="button"
              className={`segmented-item ${
                approvalFilter === value ? "segmented-item-active" : ""
              }`}
              aria-pressed={approvalFilter === value}
              onClick={() => handleApprovalFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-5">
        <table className="table">
          <thead>
            <tr>
              <th className="user-header-item">User Name</th>
              <th className="user-header-item">Company</th>
              <th className="user-header-item header-item-center">
                Account Status
              </th>
              <th className="user-header-item header-item-center">Usage Req</th>
              <th className="user-header-item header-item-center">
                Plan Expiry
              </th>
              <th className="user-header-item header-item-center">
                Created on
              </th>
              <th className="user-header-item header-item-center">Status</th>
              <th className="user-header-item header-item-center">Plan</th>
              <th className="user-header-item header-item-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user._id} className="data-row-verticle-middle">
                <td className="user-data-item data-item-captilize">
                  {user.firstName} {user.lastName}
                </td>
                <td className="user-data-item data-item-captilize">
                  {user.companyName}
                </td>
                <td className="user-data-item data-item-center">
                  <AccountStatusBadge user={user} />
                </td>
                <td className="user-data-item data-item-center data-item-uppercase">
                  {formatNumber(user.usageReq)}
                </td>
                <td className="user-data-item data-item-center data-item-uppercase">
                  Dec 29, 2022
                </td>
                <td className="user-data-item data-item-center">
                  {moment(user.createdAt).format("MMM D, YYYY [at] hh:mm A")}
                </td>
                <td className="bg-transparent">
                  <select
                    id={user._id}
                    className="form-select primary-form-control"
                    onChange={handleStatusChange}
                    defaultValue={user.isActive ? "active" : "suspended"}
                  >
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </td>
                <td className="bg-transparent">
                  <select
                    className="form-select primary-form-control"
                    onChange={(e) => handlePlanChange(user._id, e.target.value)}
                    defaultValue={user?.subscribedPlan?.planId}
                  >
                    {plans.map((item) => (
                      <option key={item._id} value={item._id}>
                        {item?.name && toTitleCase(item?.name)}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="user-data-item data-item-center">
                  <div className="d-flex justify-content-center align-items-center gap-1">
                    <ApprovalActions user={user} onApproval={handleApproval} />
                    <button
                      type="button"
                      onClick={() => setEditingUser(user)}
                      className="btn"
                      title="Edit user"
                      aria-label="Edit user"
                    >
                      <MdEdit />
                    </button>
                    <button
                      type="button"
                      onClick={() => setPasswordUser(user)}
                      className="btn"
                      title="Change password"
                      aria-label="Change password"
                    >
                      <MdOutlineVpnKey />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(user._id)}
                      className="btn btn-danger btn-sm"
                      title="Delete user"
                      aria-label="Delete user"
                    >
                      <AiFillDelete />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div>
            Showing {(page - 1) * USERS_PER_PAGE + 1} to{" "}
            {Math.min(page * USERS_PER_PAGE, filteredUsers.length)} of{" "}
            {filteredUsers.length} entries
          </div>
          <div className="d-flex gap-2 align-items-center">
            <button
              onClick={handlePrevPage}
              disabled={page === 1}
              className={`btn ${
                page === 1 ? "btn-outline-secondary" : "btn-primary"
              }`}
            >
              Previous
            </button>

            <span className="mx-3">
              Page {page} of {totalPages}
            </span>

            <button
              onClick={handleNextPage}
              disabled={page === totalPages}
              className={`btn ${
                page === totalPages ? "btn-outline-secondary" : "btn-primary"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {editingUser && (
        <EditUserDialog
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onUpdated={() => {
            setEditingUser(null);
            fetchData();
          }}
        />
      )}
      {passwordUser && (
        <ChangePasswordDialog
          user={passwordUser}
          onClose={() => setPasswordUser(null)}
        />
      )}
    </div>
  );
};

export default UsersList;
